import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ReturnDetailDto, ReturnSummaryDto, ReturnUpdateDto, CreateReturnRequest, CreateAssessmentRequest, AssessmentCaseDto, CreateStaffPlanRequest, StaffPlanResult, CreateLienRequest, LienCaseDto, ReturnSearchCriteria, PlanPreview, PlanDetail, RollupAssessmentRequest } from '../../models/return.model';
import { PaymentRequestDto, PaymentDto, ReceiptDto, BasketDto, PaymentMethodDto } from '../../models/payment.model';
import { FeeDto, CreateFeeRequestDto, AllowanceDto, CreateAllowanceRequestDto } from '../../models/cashering.model';
import { Page } from '../../models/page.model';

@Injectable({
  providedIn: 'root'
})
export class CasheringService {
  private baseUrl = `${environment.apiUrl}/api`;

  constructor(private http: HttpClient) { }

  // ===== Return Management =====

  getAllReturns(page: number = 0, size: number = 20): Observable<Page<ReturnSummaryDto>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<Page<ReturnSummaryDto>>(`${this.baseUrl}/returns`, { params });
  }

  createReturn(request: CreateReturnRequest, userId?: string, userName?: string): Observable<ReturnDetailDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<ReturnDetailDto>(`${this.baseUrl}/returns`, request, { headers });
  }

  createAssessment(request: CreateAssessmentRequest, userId?: string, userName?: string): Observable<AssessmentCaseDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<AssessmentCaseDto>(`${this.baseUrl}/admin/assessments`, request, { headers });
  }

  createStaffPlan(request: CreateStaffPlanRequest, userId?: string, userName?: string): Observable<StaffPlanResult> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<StaffPlanResult>(`${this.baseUrl}/admin/installment-plans`, request, { headers });
  }

  createLien(request: CreateLienRequest, userId?: string, userName?: string): Observable<LienCaseDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<LienCaseDto>(`${this.baseUrl}/admin/liens`, request, { headers });
  }

  // ===== Plan/assessment/lien previews, detail, cancel =====

  previewStaffPlan(request: CreateStaffPlanRequest): Observable<PlanPreview> {
    return this.http.post<PlanPreview>(`${this.baseUrl}/admin/installment-plans/preview`, request);
  }

  getPlanDetail(planId: number): Observable<PlanDetail> {
    return this.http.get<PlanDetail>(`${this.baseUrl}/admin/installment-plans/${planId}`);
  }

  cancelStaffPlan(planId: number, userId?: string): Observable<void> {
    const headers = this.createUserHeaders(userId);
    return this.http.delete<void>(`${this.baseUrl}/admin/installment-plans/${planId}`, { headers });
  }

  previewRollupAssessment(request: RollupAssessmentRequest): Observable<PlanPreview> {
    return this.http.post<PlanPreview>(`${this.baseUrl}/admin/assessments/rollup/preview`, request);
  }

  createRollupAssessment(request: RollupAssessmentRequest, userId?: string, userName?: string): Observable<AssessmentCaseDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<AssessmentCaseDto>(`${this.baseUrl}/admin/assessments/rollup`, request, { headers });
  }

  cancelAssessment(caseId: number, userId?: string): Observable<void> {
    const headers = this.createUserHeaders(userId);
    return this.http.delete<void>(`${this.baseUrl}/admin/assessments/${caseId}`, { headers });
  }

  previewLien(request: CreateLienRequest): Observable<PlanPreview> {
    return this.http.post<PlanPreview>(`${this.baseUrl}/admin/liens/preview`, request);
  }

  cancelLien(caseId: number, userId?: string): Observable<void> {
    const headers = this.createUserHeaders(userId);
    return this.http.delete<void>(`${this.baseUrl}/admin/liens/${caseId}`, { headers });
  }

  getReturnById(id: number): Observable<ReturnDetailDto> {
    return this.http.get<ReturnDetailDto>(`${this.baseUrl}/returns/${id}`);
  }

  /** Advanced query: filter returns across all accounts by the given criteria. */
  searchReturns(criteria: ReturnSearchCriteria, page = 0, size = 100): Observable<Page<ReturnSummaryDto>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.post<Page<ReturnSummaryDto>>(`${this.baseUrl}/returns/search`, criteria, { params });
  }

  getReturnsByAccount(accountId: number): Observable<ReturnSummaryDto[]> {
    return this.http.get<any>(`${this.baseUrl}/returns/account/${accountId}`).pipe(
      // Endpoint returns a List, but guard against a paginated shape just in case.
      map(res => (Array.isArray(res) ? res : (res?.content || [])) as ReturnSummaryDto[])
    );
  }

  getOutstandingReturns(accountId: number): Observable<ReturnSummaryDto[]> {
    return this.http.get<any>(`${this.baseUrl}/returns/account/${accountId}/outstanding`).pipe(
      map(response => {
        const items = Array.isArray(response) ? response : (response?.content || []);
        return items as ReturnSummaryDto[];
      })
    );
  }

  updateReturn(returnId: number, returnData: ReturnUpdateDto, userId?: string, userName?: string): Observable<ReturnDetailDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.put<ReturnDetailDto>(`${this.baseUrl}/returns/${returnId}`, returnData, { headers });
  }

  submitReturn(returnId: number, userId?: string, userName?: string): Observable<ReturnDetailDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<ReturnDetailDto>(`${this.baseUrl}/returns/${returnId}/submit`, {}, { headers });
  }

  submitZeroReturn(returnId: number, userId?: string, userName?: string): Observable<ReturnDetailDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<ReturnDetailDto>(`${this.baseUrl}/returns/${returnId}/submit-zero`, {}, { headers });
  }

  toggleCustomerEditing(returnId: number, enabled: boolean, userId?: string, userName?: string): Observable<ReturnDetailDto> {
    const headers = this.createUserHeaders(userId, userName);
    const params = new HttpParams().set('enabled', enabled.toString());
    return this.http.put<ReturnDetailDto>(`${this.baseUrl}/returns/${returnId}/customer-editing`, {}, { headers, params });
  }

  // ===== Fee Management =====

  getFees(returnId: number): Observable<FeeDto[]> {
    return this.http.get<FeeDto[]>(`${this.baseUrl}/returns/${returnId}/fees`);
  }

  addFee(returnId: number, request: CreateFeeRequestDto, userId?: string, userName?: string): Observable<FeeDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<FeeDto>(`${this.baseUrl}/returns/${returnId}/fees`, request, { headers });
  }

  toggleFee(returnId: number, feeId: number, enabled: boolean, userId?: string, userName?: string): Observable<FeeDto> {
    const headers = this.createUserHeaders(userId, userName);
    const params = new HttpParams().set('enabled', enabled.toString());
    return this.http.put<FeeDto>(`${this.baseUrl}/returns/${returnId}/fees/${feeId}`, {}, { headers, params });
  }

  removeFee(returnId: number, feeId: number, userId?: string, userName?: string): Observable<void> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.delete<void>(`${this.baseUrl}/returns/${returnId}/fees/${feeId}`, { headers });
  }

  // ===== Allowance Management =====

  getAllowances(returnId: number): Observable<AllowanceDto[]> {
    return this.http.get<AllowanceDto[]>(`${this.baseUrl}/returns/${returnId}/allowances`);
  }

  addAllowance(returnId: number, request: CreateAllowanceRequestDto, userId?: string, userName?: string): Observable<AllowanceDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<AllowanceDto>(`${this.baseUrl}/returns/${returnId}/allowances`, request, { headers });
  }

  toggleAllowance(returnId: number, allowanceId: number, enabled: boolean, userId?: string, userName?: string): Observable<AllowanceDto> {
    const headers = this.createUserHeaders(userId, userName);
    const params = new HttpParams().set('enabled', enabled.toString());
    return this.http.put<AllowanceDto>(`${this.baseUrl}/returns/${returnId}/allowances/${allowanceId}`, {}, { headers, params });
  }

  removeAllowance(returnId: number, allowanceId: number, userId?: string, userName?: string): Observable<void> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.delete<void>(`${this.baseUrl}/returns/${returnId}/allowances/${allowanceId}`, { headers });
  }

  // ===== Notes Management =====

  getNotes(returnId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/returns/${returnId}/notes`);
  }

  addNote(returnId: number, content: string, userId?: string, userName?: string, sendToRegistrant = false): Observable<any> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<any>(`${this.baseUrl}/returns/${returnId}/notes`, { content, sendToRegistrant }, { headers });
  }

  // ===== Attachments Management =====

  getAttachments(returnId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/returns/${returnId}/attachments`);
  }

  uploadAttachment(returnId: number, file: File, userId?: string, userName?: string): Observable<any> {
    const headers = this.createUserHeaders(userId, userName);
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.baseUrl}/returns/${returnId}/attachments`, formData, { headers });
  }

  deleteAttachment(returnId: number, attachmentId: number, userId?: string, userName?: string): Observable<void> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.delete<void>(`${this.baseUrl}/returns/${returnId}/attachments/${attachmentId}`, { headers });
  }

  // NOTE: Attachment download is not a documented standalone endpoint.
  // When the backend adds this, update the path accordingly.
  downloadAttachment(returnId: number, attachmentId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/returns/${returnId}/attachments/${attachmentId}/download`, { responseType: 'blob' });
  }

  // ===== History =====

  getHistory(returnId: number, page?: number, size?: number): Observable<any> {
    if (page !== undefined && size !== undefined) {
      const params = new HttpParams()
        .set('page', page.toString())
        .set('size', size.toString());
      return this.http.get<any>(`${this.baseUrl}/returns/${returnId}/history`, { params });
    }
    return this.http.get<any[]>(`${this.baseUrl}/returns/${returnId}/history`);
  }

  // ===== Payment Processing =====

  processPayment(request: PaymentRequestDto, userId?: string, userName?: string): Observable<PaymentDto> {
    const headers = this.createUserHeaders(userId, userName);
    return this.http.post<PaymentDto>(`${this.baseUrl}/payments`, request, { headers });
  }

  getPaymentById(paymentId: number): Observable<PaymentDto> {
    return this.http.get<PaymentDto>(`${this.baseUrl}/payments/${paymentId}`);
  }

  getPaymentByReceiptNumber(receiptNumber: string): Observable<PaymentDto> {
    return this.http.get<PaymentDto>(`${this.baseUrl}/payments/receipt/${receiptNumber}`);
  }

  getPaymentsByAccount(accountId: number, page?: number, size?: number): Observable<any> {
    if (page !== undefined && size !== undefined) {
      const params = new HttpParams()
        .set('page', page.toString())
        .set('size', size.toString());
      return this.http.get<any>(`${this.baseUrl}/payments/account/${accountId}`, { params });
    }
    return this.http.get<PaymentDto[]>(`${this.baseUrl}/payments/account/${accountId}`);
  }

  getReceipt(paymentId: number): Observable<ReceiptDto> {
    return this.http.get<ReceiptDto>(`${this.baseUrl}/payments/${paymentId}/receipt`);
  }

  // ===== Basket Management =====

  createBasket(accountId: number, paymentType: string = 'WALK_IN', officeId?: string, officeName?: string, userId?: string): Observable<BasketDto> {
    let params = new HttpParams().set('accountId', accountId.toString());
    if (paymentType) params = params.set('paymentType', paymentType);
    if (officeId) params = params.set('officeId', officeId);
    if (officeName) params = params.set('officeName', officeName);

    const headers = userId ? new HttpHeaders({ 'X-User-Id': String(userId) }) : undefined;
    return this.http.post<BasketDto>(`${this.baseUrl}/payments/basket`, {}, { params, headers });
  }

  getBasketById(basketId: number): Observable<BasketDto> {
    return this.http.get<BasketDto>(`${this.baseUrl}/payments/basket/${basketId}`);
  }

  // NOTE: /api/payments/basket/active is not a documented backend endpoint.
  // Use createBasket() to start a new basket session for an account.
  getActiveBasket(accountId: number, userId?: string): Observable<BasketDto | null> {
    return this.createBasket(accountId, 'WALK_IN', undefined, undefined, userId);
  }

  updateBasketPaymentType(basketId: number, paymentType: string): Observable<BasketDto> {
    const params = new HttpParams().set('paymentType', paymentType);
    return this.http.put<BasketDto>(`${this.baseUrl}/payments/basket/${basketId}`, {}, { params });
  }

  addReturnToBasket(basketId: number, returnId: number): Observable<BasketDto> {
    const params = new HttpParams().set('returnId', returnId.toString());
    return this.http.post<BasketDto>(`${this.baseUrl}/payments/basket/${basketId}/items`, {}, { params });
  }

  removeReturnFromBasket(basketId: number, returnId: number): Observable<BasketDto> {
    return this.http.delete<BasketDto>(`${this.baseUrl}/payments/basket/${basketId}/items/${returnId}`);
  }

  deleteBasket(basketId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/payments/basket/${basketId}`);
  }

  calculatePaymentTotals(basketId: number, paymentMethods: PaymentMethodDto[]): Observable<BasketDto> {
    return this.http.post<BasketDto>(`${this.baseUrl}/payments/basket/${basketId}/calculate`, paymentMethods);
  }

  // NOTE: toggleStandardFee is not a documented backend endpoint.
  // Use toggleFee(returnId, feeId, enabled) with a known fee ID instead.
  toggleStandardFee(returnId: number, feeId: number, enabled: boolean, userId?: string, userName?: string): Observable<FeeDto> {
    return this.toggleFee(returnId, feeId, enabled, userId, userName);
  }

  // NOTE: These convenience overloads route through the canonical endpoints on the returns resource.
  deleteFee(returnId: number, feeId: number, userId?: string, userName?: string): Observable<void> {
    return this.removeFee(returnId, feeId, userId, userName);
  }

  deleteAllowance(returnId: number, allowanceId: number, userId?: string, userName?: string): Observable<void> {
    return this.removeAllowance(returnId, allowanceId, userId, userName);
  }

  // NOTE: Voucher printing is not a documented backend endpoint; implement when available.
  printVoucher(returnId: number): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/returns/${returnId}/voucher`, { responseType: 'blob' });
  }

  // ===== Helper Methods =====

  private createUserHeaders(userId?: string, userName?: string): HttpHeaders {
    let headers = new HttpHeaders();
    if (userId) {
      // Coerce to string: HttpHeaders spreads the value internally and throws
      // a TypeError if it is a number (e.g. a Long user id that slipped through).
      headers = headers.set('X-User-Id', String(userId));
    }
    if (userName) {
      headers = headers.set('X-User-Name', String(userName));
    }
    return headers;
  }
}
